import { createContext, useState, useEffect, use } from "react";
import { jwtDecode } from "jwt-decode";

import useLoader from "./useLoader"; // Assuming you put the loader hook in hooks folder
import { useNavigate } from "react-router-dom";
import authService from "../services/authService";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {

    const navigate = useNavigate();

    // Add initialization state to prevent race conditions
    const [isInitialized, setIsInitialized] = useState(false);

    const [authTokens, setAuthTokens] = useState(null);
    
    const [user, setUser] = useState(null);

    // Use the loader hook
    const {
        isLoading,
        loadingMessage,
        loadingType,
        showLoader,
        hideLoader,
        showSuccess,
        showError
    } = useLoader();

    // Error message state
    const [authError, setAuthError] = useState(null);

    // Clear error when needed
    const clearError = () => {
        setAuthError(null);
    };

    // Get user-friendly error message based on status and response
    const getErrorMessage = (status, data = null) => {
        // If we have detailed error from backend
        if(!status){
            if (data) {
                // Handle field-specific errors
                if (data.email) {
                    return `Email: ${data.email[0]}`;
                }
                if (data.password) {
                    return `Mot de passe: ${data.password[0]}`;
                }
                if (data.non_field_errors) {
                    return data.non_field_errors[0];
                }
                if (data.detail) {
                    return data.detail;
                }
            }
        }
      

        // Handle errors based on HTTP status codes
        switch (status) {
            case 400:
                return "Données de connexion invalides";
            case 401:
                return "Email ou mot de passe incorrect";
            case 403:
                return "Accès refusé";
            case 404:
                return "Service non trouvé";
            case 422:
                return "Données de formulaire invalides";
            case 429:
                return "Trop de tentatives. Veuillez patienter avant de réessayer";
            case 500:
                return "Erreur du serveur. Veuillez réessayer plus tard";
            case 502:
                return "Service temporairement indisponible";
            case 503:
                return "Service en maintenance";
            case 0:
                return "Problème de connexion. Vérifiez votre connexion internet";
            default:
                return "Une erreur inattendue s'est produite";
        }
    };

    const loginUser = async (e) => {
        e.preventDefault();
        
        // Clear previous errors
        clearError();
        
        // Get form data
        const formData = new FormData(e.target);
        const email = formData.get('email');
        const password = formData.get('password');
        const rememberMe = formData.get('rememberMe');

        // Basic validation
        if (!email || !password) {
            const errorMsg = "Veuillez saisir votre email et mot de passe";
            setAuthError(errorMsg);
            showError(errorMsg);
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            const errorMsg = "Veuillez saisir un email valide";
            setAuthError(errorMsg);
            showError(errorMsg);
            return;
        }

        try {
            // Show loading
            showLoader("Connexion en cours...", "loading");

            // Make login request
            const result = await authService.login(email, password);
            // const result = await authService.login(null, null);

            if (result.success) {
                // Extract tokens and user data
                const { access, refresh } = result.data;
                
                // Decode JWT to get user info (you might want to use a JWT library for this)
                const userPayload = jwtDecode(access);
                
                const userData = {
                    id: userPayload.user_id,
                    email: userPayload.email,
                    firstName: userPayload.first_name,
                    lastName: userPayload.last_name,
                    isActive: userPayload.is_active,
                    organization: userPayload.organization,
                    roles: userPayload.roles,
                    role: userPayload.role,
                    is_staff: userPayload.is_staff,
                    is_superuser: userPayload.is_superuser
                };

                const tokens = { access, refresh };

                // Update state
                setAuthTokens(tokens);
                setUser(userData);

                // console.log('Login successful - tokens:', tokens);
                console.log('Login successful - user data:', userData);

                // Store in localStorage based on rememberMe
                if (rememberMe) {
                    localStorage.setItem('authTokens', JSON.stringify(tokens));
                    localStorage.setItem('user', JSON.stringify(userData));
                } else {
                    // Store in sessionStorage for session-only persistence
                    sessionStorage.setItem('authTokens', JSON.stringify(tokens));
                    sessionStorage.setItem('user', JSON.stringify(userData));
                }

                // Show success and redirect
                showSuccess(`Bienvenue ${userData.firstName}!`);
                
                // You might want to redirect here or let the parent component handle it
                setTimeout(() => {
                    // Redirect to dashboard or wherever needed

                    if (userData.organization) {
                        if(userData.roles && userData.roles.length > 0){
                            navigate("/dashboard");
                        }else{
                            showError("User has no role assigned");
                            navigate("/login");
                        }
                    }else{
                        showError("Informations sur l'organisation absentes ou sans rôle");
                        navigate("/login");
                    }
                    
                    
                }, 1500);

            } else {
                // Handle login failure using status code
                const errorMessage = getErrorMessage(result.status, result.errorData);
                setAuthError(errorMessage);
                showError(errorMessage);
            }

        } catch (error) {
            console.error('Login error:', error);
            const errorMessage = getErrorMessage(0); // Network error
            setAuthError(errorMessage);
            showError(errorMessage);
        }
    };



        const loginSuperAdmin = async (formDataObj) => {
        // Clear previous errors
        clearError();

        // Extract email and password from the form data object
        const email = formDataObj?.email || formDataObj?.target?.email?.value || '';
        const password = formDataObj?.password || formDataObj?.target?.password?.value || '';
        const rememberMe = formDataObj?.rememberMe || formDataObj?.target?.rememberMe?.value || false;

        // Basic validation
        if (!email || !password) {
            const errorMsg = "Veuillez saisir votre email et mot de passe";
            setAuthError(errorMsg);
            showError(errorMsg);
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            const errorMsg = "Veuillez saisir un email valide";
            setAuthError(errorMsg);
            showError(errorMsg);
            return;
        }

        try {
            // Show loading
            showLoader("Connexion en cours...", "loading");

            // Make login request
            const result = await authService.login(email, password);

            if (result.success) {
                // Extract tokens and user data
                const { access, refresh } = result.data;

                // Decode JWT to get user info
                const userPayload = jwtDecode(access);

                const userData = {
                    id: userPayload.user_id,
                    email: userPayload.email,
                    firstName: userPayload.first_name,
                    lastName: userPayload.last_name,
                    role_fuction: userPayload.role_fuction,
                    isActive: userPayload.is_active,
                    organization: userPayload.organization,
                    roles: userPayload.roles,
                    role: userPayload.role,
                    is_staff: userPayload.is_staff,
                    is_superuser: userPayload.is_superuser
                };

                const tokens = { access, refresh };

                // Update state
                setAuthTokens(tokens);
                setUser(userData);

                console.log('Login successful - user data:', userData);

                // Store in localStorage based on rememberMe
                if (rememberMe) {
                    localStorage.setItem('authTokens', JSON.stringify(tokens));
                    localStorage.setItem('user', JSON.stringify(userData));
                } else {
                    // Store in sessionStorage for session-only persistence
                    sessionStorage.setItem('authTokens', JSON.stringify(tokens));
                    sessionStorage.setItem('user', JSON.stringify(userData));
                }

                // Show success and redirect
                showSuccess(`Bienvenue ${userData.firstName}!`);

                setTimeout(() => {
                    // Redirect to dashboard or wherever needed
                    if (userData.is_staff && userData.is_superuser) {
                        navigate("/admin-panel");
                    } else {
                        showError("Invalid credentials");
                        navigate("/login");
                    }
                }, 1500);

            } else {
                // Handle login failure using status code
                const errorMessage = getErrorMessage(result.status, result.errorData);
                setAuthError(errorMessage);
                showError(errorMessage);
            }

        } catch (error) {
            console.error('Login error:', error);
            const errorMessage = getErrorMessage(0); // Network error
            setAuthError(errorMessage);
            showError(errorMessage);
        }
    };




    const logoutUser = async () => {
        console.log('Logging out user...');
        
        // Call logout API to log the session (if user is authenticated)
        if (authTokens?.access ) {
            try {
                await authService.logout(authTokens.access, authTokens.refresh);
            } catch (error) {
                console.warn('Failed to log logout session, but continuing with logout:', error);
            }
        }
        
        setAuthTokens(null);
        setUser(null);
        setAuthError(null);
        setIsInitialized(true); // Keep initialized state as true after logout
        
        // Clear storage
        localStorage.removeItem('authTokens');
        localStorage.removeItem('user');
        sessionStorage.removeItem('authTokens');
        sessionStorage.removeItem('user');
        
        console.log('User logged out successfully');
        
        // Redirect to login
        navigate("/login");
    };

    // Auto-refresh token
    const refreshToken = async () => {
        if (!authTokens?.refresh) return false;

        try {
            console.log('Attempting to refresh token...');
            const result = await authService.refreshToken(authTokens.refresh);
            
            if (result.success) {
                // CRITICAL FIX: Handle token rotation properly
                // When ROTATE_REFRESH_TOKENS is True, backend returns both new access AND refresh tokens
                const newTokens = {
                    access: result.access_token,
                    refresh: result.refresh_token || authTokens.refresh // Use new refresh token if provided, fallback to old one
                };
                
                console.log('Token refresh successful:', {
                    oldRefreshToken: authTokens.refresh.substring(0, 20) + '...',
                    newAccessToken: newTokens.access.substring(0, 20) + '...',
                    newRefreshToken: newTokens.refresh.substring(0, 20) + '...'
                });
                
                setAuthTokens(newTokens);
                
                // Update storage
                if (localStorage.getItem('authTokens')) {
                    localStorage.setItem('authTokens', JSON.stringify(newTokens));
                } else if (sessionStorage.getItem('authTokens')) {
                    sessionStorage.setItem('authTokens', JSON.stringify(newTokens));
                }
                
                return true;
            } else {
                console.error('Token refresh failed:', result.error);
                // Refresh failed, logout user
                logoutUser();
                return false;
            }
        } catch (error) {
            console.error('Token refresh error:', error);
            logoutUser();
            return false;
        }
    };

    // Check if user is authenticated - make it a computed property, not a function
    const isAuthenticated = !!(authTokens?.access && user && isInitialized);

    // Role checking utility function
    const hasRole = (roleName) => {
        if (!user?.roles || !Array.isArray(user.roles)) return false;
        return user.roles.some(userRole => 
            userRole.toLowerCase() === roleName.toLowerCase()
        );
    };

    // Check if user has any of the specified roles
    const hasAnyRole = (roleNames) => {
        if (!Array.isArray(roleNames)) return hasRole(roleNames);
        return roleNames.some(roleName => hasRole(roleName));
    };

    // Check if user has all of the specified roles
    const hasAllRoles = (roleNames) => {
        if (!Array.isArray(roleNames)) return hasRole(roleNames);
        return roleNames.every(roleName => hasRole(roleName));
    };

    // Initialize auth state from storage
    useEffect(() => {
        const initializeAuth = () => {
            // Try localStorage first, then sessionStorage
            let tokens = localStorage.getItem('authTokens');
            let userData = localStorage.getItem('user');
            
            if (!tokens) {
                tokens = sessionStorage.getItem('authTokens');
                userData = sessionStorage.getItem('user');
            }

            if (tokens && userData) {
                try {
                    const parsedTokens = JSON.parse(tokens);
                    const parsedUser = JSON.parse(userData);
                    
                    setAuthTokens(parsedTokens);
                    setUser(parsedUser);
                    
                    // console.log('Auth initialized from storage - tokens:', parsedTokens);
                    // console.log('Auth initialized from storage - user:', parsedUser);
                } catch (error) {
                    console.error('Error parsing stored auth data:', error);
                    // Clear invalid data
                    localStorage.removeItem('authTokens');
                    localStorage.removeItem('user');
                    sessionStorage.removeItem('authTokens');
                    sessionStorage.removeItem('user');
                }
            }
            
            // Mark initialization as complete
            setIsInitialized(true);
        };

        initializeAuth();
    }, []);

    // Auto-refresh token before it expires
    useEffect(() => {
        if (!authTokens?.access) return;

        try {
               // Decode the token safely
            const tokenPayload = jwtDecode(authTokens.access);

            const expirationTime = tokenPayload.exp * 1000; // exp is usually in seconds
            const currentTime = Date.now();
            const timeUntilExpiration = expirationTime - currentTime;

            // console.log("Decoded token:", tokenPayload);
            // console.log("Token expires in:", timeUntilExpiration, "ms");
            
            // Refresh token 5 minutes before expiration
            const refreshTime = Math.max(timeUntilExpiration - (5 * 60 * 1000), 1000);
            
            if (refreshTime > 0) {
                const refreshTimer = setTimeout(() => {
                    refreshToken();
                }, refreshTime);

                return () => clearTimeout(refreshTimer);
            }
        } catch (error) {
            console.error('Error parsing token for auto-refresh:', error);
        }
    }, [authTokens]);

    const contextData = {
        // State
        user,
        authTokens,
        authError,
        isAuthenticated, // Now a computed property, not a function call
        isInitialized, // Add this to help components know when auth is ready
        
        // Loading state
        isLoading,
        loadingMessage,
        loadingType,
        
        // Actions
        loginUser,
        loginSuperAdmin,
        logoutUser,
        refreshToken,
        clearError,
        
        // Role checking utilities
        hasRole,
        hasAnyRole,
        hasAllRoles,
        
        // Loader controls (in case you need them in components)
        showLoader,
        hideLoader,
        showSuccess,
        showError
    };

    return (
        <AuthContext.Provider value={contextData}>
            {children}
        </AuthContext.Provider>
    );
};